import { Badge, Button, Dropdown, Popover, Spin, Tabs } from "antd";
import { BellOutlined, DownOutlined, MoreOutlined } from "@ant-design/icons";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { RootState } from "../../store/store";
import CommonIcons from "../../assets/icons";
import
{
  deleteNotificationAsync,
  loadMoreNotificationsAsync,
  markAllNotificationsReadAsync,
  markNotificationReadAsync,
} from "../../store/reducers/notificationSlice";

export default function NotificationPanel({
  onClose,
}: {
  onClose?: () => void;
})
{
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const notifications = useSelector((state: RootState) => state.notificationSlice.total) || [];
  const loading = useSelector((state: RootState) => state.notificationSlice.loading);
  const loadingMore = useSelector((state: RootState) => state.notificationSlice.loadingMore);
  const hasMore = useSelector((state: RootState) => state.notificationSlice.hasMore);
  const totalCount = useSelector((state: RootState) => state.notificationSlice.totalCount);
  const unreadCount = useSelector((state: RootState) => state.notificationSlice.unreadCount);

  const [activeTab, setActiveTab] = React.useState<string>("all");
  const [detailOpenId, setDetailOpenId] = React.useState<string | number | null>(null);

  const handleMarkAllAsRead = () =>
  {
    dispatch(markAllNotificationsReadAsync());
  };

  const handleDelete = (id: string | number) =>
  {
    dispatch(deleteNotificationAsync(id));
  };

  const handleLoadMore = () =>
  {
    dispatch(loadMoreNotificationsAsync());
  };

  const filteredNotifications =
    activeTab === "all" ? notifications : notifications.filter((n) => !n.read);

  // On the unread tab, stop offering "View more" once every unread item is shown.
  const showLoadMore =
    hasMore &&
    (activeTab === "all" || filteredNotifications.length < unreadCount);

  const tabItems = [
    { key: "all", label: `${t("tabs.all")} (${totalCount})` },
    { key: "unread", label: `${t("tabs.unread")} (${unreadCount})` },
  ];

  const handleScroll = React.useCallback(() =>
  {
    setDetailOpenId(null);
  }, []);

  return (
    <div className="w-[380px] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 pb-2">
        <h3 className="m-0 text-base font-semibold text-[var(--color-text-primary)] flex items-center gap-2">
          {t("notifications.title")}

        </h3>
      </div>

      {/* Tabs */}
      <div
        className="flex justify-between items-center pr-4 pl-[6px]"
        style={{ borderBottom: "1px solid #61687E" }}
      >
        <Tabs
          activeKey={activeTab}
          items={tabItems}
          onChange={setActiveTab}
          className="notification-tabs"
          tabBarStyle={{ marginBottom: -1 }}
          size="small"
        />
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="text-xs text-blue-500 hover:text-blue-400 transition-colors flex items-center gap-1"
          >
            <CommonIcons.MarkReadAllIcon />
            {t("markAllRead")}
          </button>
        )}
      </div>

      {/* Notification List */}
      <Spin spinning={loading}>
        <div className="py-3">
          {filteredNotifications.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              {t("noNotifications")}
            </div>
          ) : (
            <>
              <div
                className="overflow-y-auto max-h-[420px] custom-scrollbar"
                onScroll={handleScroll}
              >
                {filteredNotifications.map((notification, ind) =>
                {
                  const isOpen = detailOpenId === notification.id;

                  // Resolve the notification's target route (if the tag looks like one).
                  const tag = notification?.tags?.[0];
                  const target =
                    tag && tag.includes("/")
                      ? tag.startsWith("/")
                        ? tag
                        : `/${tag}`
                      : null;
                  // Can navigate only when there's a route target and we're not already on it.
                  const canNavigate = !!target && target !== window.location.pathname;

                  const handleNavigateDetail = () =>
                  {
                    if (!canNavigate) return;
                    navigate(target as string);
                    setDetailOpenId(null);
                    onClose?.();
                  };

                  const detailContent = (
                    <div
                      className={`notification-detail-content p-3 max-w-[360px] ${canNavigate
                        ? "cursor-pointer rounded hover:bg-[var(--neutral-800)] transition-colors"
                        : ""
                        }`}
                      onClick={handleNavigateDetail}
                    >
                      <h4 className="font-semibold mb-2 text-[var(--color-text-primary)]">
                        {notification.title}
                      </h4>
                      {notification.message && (
                        <p className="text-sm text-[var(--color-text-secondary)] mb-2">
                          {notification.message}
                        </p>
                      )}
                      <div className="text-xs text-[var(--color-text-tertiary)]">
                        {moment(notification.createAt).format("HH:mm DD/MM/YYYY")}
                      </div>
                      {canNavigate && (
                        <div className="text-xs text-blue-400 mt-2 hover:text-blue-300">
                          {t("View detail")} →
                        </div>
                      )}
                    </div>
                  );

                  // Clicking a notification opens the detail popover to read it.
                  // Navigation to the order detail only happens when the detail is clicked.
                  const handleClickNotification = () =>
                  {
                    dispatch(markNotificationReadAsync(notification.id));
                    setDetailOpenId(notification.id);
                  };

                  return (
                    <Popover
                      key={notification.id}
                      overlayClassName="popover-detail"
                      open={isOpen}
                      onOpenChange={(newOpen) =>
                      {
                        if (!newOpen)
                        {
                          setDetailOpenId(null);
                        } else
                        {
                          dispatch(markNotificationReadAsync(notification.id));
                          setDetailOpenId(notification.id);
                        }
                      }}
                      trigger="contextMenu"
                      placement="right"
                      getPopupContainer={() => document.body}
                      content={detailContent}
                      showArrow={false}
                    >
                      <div
                        className={`flex gap-3 p-3 mb-1 rounded cursor-pointer transition-colors hover:bg-[var(--neutral-800)] ${notification.read
                          ? "bg-transparent"
                          : "bg-[var(--neutral-900)]"
                          }`}
                        onClick={handleClickNotification}
                        style={{ borderBottom: ind < filteredNotifications?.length - 1 ? '1px solid #61687E' : 'none' }}
                      >
                        {/* LEFT ICON */}
                        <div className="flex-shrink-0 mt-0.5">
                          <BellOutlined
                            className={`text-lg ${notification.read
                              ? " !text-[#ff8c42]"
                              : " !text-[#ff8c42]"
                              }`}
                          />
                        </div>

                        {/* CONTENT */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p
                              className={`mb-1 line-clamp-1 ${notification.read
                                ? "text-[var(--color-text-secondary)]"
                                : "font-semibold text-[var(--color-text-primary)]"
                                }`}
                            >
                              {notification.title}
                            </p>
                            <Dropdown
                              trigger={["click"]}
                              menu={{
                                items: [
                                  {
                                    key: "delete",
                                    label: t("Delete"),
                                    danger: true,
                                    onClick: ({ domEvent }) =>
                                    {
                                      domEvent.stopPropagation();
                                      handleDelete(notification.id);
                                    },
                                  },
                                ],
                              }}
                            >
                              <MoreOutlined
                                className="text-gray-400 hover:text-white"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Dropdown>
                          </div>

                          {notification.message && (
                            <p className="text-xs text-[var(--color-text-secondary)] line-clamp-2 mb-1">
                              {notification.message}
                            </p>
                          )}

                          <div className="flex items-center justify-between">
                            <p className="text-[11px] text-[var(--color-text-tertiary)]">
                              {moment(notification.createAt).fromNow()}
                            </p>
                            {!notification.read && (
                              <Badge dot status="processing" />
                            )}
                          </div>
                        </div>
                      </div>
                    </Popover>
                  );
                })}
              </div>

              {/* View more */}
              {showLoadMore && (
                <div className="flex justify-center py-2 border-t border-[#61687E]">
                  <Button
                    type="text"
                    onClick={handleLoadMore}
                    loading={loadingMore}
                    className="text-blue-400 hover:!text-blue-300"
                  >
                    {t("View more")} <DownOutlined />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Spin>
    </div>
  );
}
